import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { JobListItem } from "./JobListItem";
import { JobListSkeleton } from "./JobListSkeleton";
import { EmptyJobsState } from "./EmptyJobsState";
import { RecruitmentStatus } from "@/lib/schema";

interface JobListing {
  id: string;
  title: string;
  rewardInAirPoints?: number;
  location?: string;
  recruitmentStatus?: RecruitmentStatus;
}

interface AvailableJobsSectionProps {
  jobs: JobListing[];
  isLoading: boolean;
  title?: string;
  viewAllHref?: string;
  viewAllText?: string;
  maxJobs?: number;
  totalJobs?: number;
  className?: string;
  testId?: string;
}

export function AvailableJobsSection({ 
  jobs, 
  isLoading, 
  title = "Available jobs",
  viewAllHref = "/jobs",
  viewAllText = "View all jobs",
  maxJobs = 4,
  totalJobs = 0,
  className = "border-0 shadow-sm bg-gray-50/50",
  testId = "card-available-jobs"
}: AvailableJobsSectionProps) {
  const displayJobs = jobs.slice(0, maxJobs);

  return (
    <Card className={className} data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <Link href={viewAllHref}>
            <Button 
              variant="link" 
              className="text-orange-500 hover:text-orange-600 font-extrabold" 
              data-testid="link-view-all-jobs"
            >
              {viewAllText}
            </Button>
          </Link>
        </div>
        <div className="space-y-3">
          {isLoading ? (
            <JobListSkeleton count={maxJobs} />
          ) : displayJobs.length > 0 ? (
            displayJobs.map((job: JobListing, index: number) => (
              <JobListItem key={job.id || index} job={job} index={index} />
            ))
          ) : (
            <EmptyJobsState />
          )}
        </div>
        {!isLoading && totalJobs > maxJobs && (
          <div className="mt-4 text-center">
            <span className="text-sm text-gray-600 font-medium">
              +{totalJobs - maxJobs} more {totalJobs - maxJobs === 1 ? 'job' : 'jobs'} available
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
